import {
  DollarAmountsField,
  type DraftDollarAmount,
} from "@platform/plan-grid-core";
import { TextField, Typography } from "@platform/design-system";

import {
  MAX_SALARY_MULTIPLIER,
  SALARY_MULTIPLIER_STEP,
  SalaryMultipliersField,
} from "./SalaryMultipliersField";
import { benefitDescriptionErrorMessage } from "./benefitDescriptionValues";

import type { BenefitDescriptionValueKind } from "./benefitDescriptionValues";
import type { ReactElement } from "react";

interface BenefitDescriptionValueInputsProps {
  valueKind: BenefitDescriptionValueKind | undefined;
  showMinimum: boolean;
  maxEntries: number;
  minEntries: number;
  showErrors: boolean;
  hasError: boolean;
  single: string;
  onSingleChange: (value: string) => void;
  onSingleBlur: () => void;
  minimum: string;
  onMinimumChange: (value: string) => void;
  arrayEntries: DraftDollarAmount[];
  onArrayUpdate: (index: number, value: string) => void;
  onArrayAdd: () => void;
  onArrayRemove: (index: number) => void;
}

interface SingleInputSpec {
  label: string;
  inputProps: Record<string, number>;
}

const SINGLE_INPUT_BY_KIND: Partial<
  Record<BenefitDescriptionValueKind, SingleInputSpec>
> = {
  dollar: { label: "Dollar Amount", inputProps: { min: 1, step: 1 } },
  percent: { label: "Percentage", inputProps: { min: 1, max: 100 } },
  multiplier: {
    label: "Salary Multiplier",
    inputProps: {
      min: SALARY_MULTIPLIER_STEP,
      max: MAX_SALARY_MULTIPLIER,
      step: SALARY_MULTIPLIER_STEP,
    },
  },
};

/**
 * Renders the value area of BenefitDescriptionCellEditor for the selected type:
 * a list of dollar amounts or salary multipliers, a single
 * dollar/percent/multiplier input, and/or the optional Minimum — whichever the
 * type calls for.
 */
export const BenefitDescriptionValueInputs = ({
  valueKind,
  showMinimum,
  maxEntries,
  minEntries,
  showErrors,
  hasError,
  single,
  onSingleChange,
  onSingleBlur,
  minimum,
  onMinimumChange,
  arrayEntries,
  onArrayUpdate,
  onArrayAdd,
  onArrayRemove,
}: BenefitDescriptionValueInputsProps): ReactElement => {
  const singleInput = valueKind ? SINGLE_INPUT_BY_KIND[valueKind] : undefined;

  return (
    <>
      {valueKind === "dollarArray" && (
        <DollarAmountsField
          entries={arrayEntries}
          showErrors={showErrors}
          maxEntries={maxEntries}
          onUpdate={onArrayUpdate}
          onAdd={onArrayAdd}
          onRemove={onArrayRemove}
        />
      )}

      {valueKind === "multiplierArray" && (
        <SalaryMultipliersField
          entries={arrayEntries}
          showErrors={showErrors}
          maxEntries={maxEntries}
          onUpdate={onArrayUpdate}
          onAdd={onArrayAdd}
          onRemove={onArrayRemove}
        />
      )}

      {singleInput && (
        <TextField
          size="sm"
          type="number"
          label={singleInput.label}
          value={single}
          onChange={(e) => onSingleChange(e.target.value)}
          onBlur={onSingleBlur}
          error={showErrors && hasError}
          inputProps={singleInput.inputProps}
          fullWidth
        />
      )}

      {showMinimum && (
        <TextField
          size="sm"
          type="number"
          label="Minimum (optional)"
          value={minimum}
          onChange={(e) => onMinimumChange(e.target.value)}
          inputProps={{ min: 0, step: 1 }}
          fullWidth
        />
      )}

      {hasError && valueKind && (
        <Typography variant="caption" color="danger">
          {benefitDescriptionErrorMessage(valueKind, minEntries)}
        </Typography>
      )}
    </>
  );
};
